import { createAppAsyncThunk } from '../../store/extensions';
import { CreatePrivateRoomDto } from './models';
import { CreateShimMemberDto } from './models/CreateShimMemberDto';
import { CreateSubgroupDto } from './models/CreateSubgroupDto';
import { LinkedRoomRelationshipType } from './models/LinkedRoom';
import {
  addMemberToPrivateRoomCall,
  createPrivateRoomCall,
  createShimMemberCall,
  createSubgroupCall,
  fetchContentForCarouselCall,
  fetchCurrentMemberInRoomCall,
  fetchFeaturedContentCall,
  fetchFeaturedStoryCall,
  fetchMemberRoomsCall,
  fetchModeratedRoomsCall,
  fetchModeratorsInTheRoomCall,
  fetchModeratorsInTheRoomCountCall,
  fetchPeopleInTheRoomCall,
  fetchPeopleInTheRoomCountCall,
  fetchPrivateRoomCodeCall,
  fetchRoomCall,
  fetchRoomsByTopicCall,
  fetchRoomSubgroupsCall,
  fetchSuggestedRoomsCall,
  findLiknedRoomsCall,
  findRoomsCall,
  joinRoomCall,
  leaveRoomCall,
  moderatorRemoveMemberFromRoomCall,
  submitConsentToJoinRoomCall,
  visitRoomCall,
} from './service';

export const fetchMemberRooms = createAppAsyncThunk(
  'identity/rooms/getAll',
  () => {
    return fetchMemberRoomsCall();
  }
);

export const fetchMyRoomSubgroups = createAppAsyncThunk(
  'community/roomSubgroups/getMySubgroups',
  (arg: { roomId: string }) => {
    return fetchRoomSubgroupsCall(arg.roomId, true, false);
  }
);

export const fetchOtherSubgroups = createAppAsyncThunk(
  'community/roomSubgroups/getOtherSubgroups',
  (arg: { roomId: string }) => {
    return fetchRoomSubgroupsCall(arg.roomId, false, false);
  }
);

export const fetchModeratorSubgroups = createAppAsyncThunk(
  'community/roomSubgroups/getModeratorSubgroups',
  (arg: { roomId: string }) => {
    return fetchRoomSubgroupsCall(arg.roomId, false, true);
  }
);

export const fetchRoomsByTopic = createAppAsyncThunk(
  'community/roomsByTopic/get',
  (topic: string) => {
    return fetchRoomsByTopicCall(topic);
  }
);

export const fetchRoom = createAppAsyncThunk(
  'community/rooms/get',
  (roomId: string) => {
    return fetchRoomCall(roomId);
  }
);

export const findRooms = createAppAsyncThunk(
  'community/rooms/search',
  (searchString: string) => {
    return findRoomsCall(searchString);
  }
);

export const joinRoom = createAppAsyncThunk(
  'community/rooms/join',
  (roomId: string) => {
    return joinRoomCall(roomId);
  }
);

export const joinSubgroup = createAppAsyncThunk(
  'community/rooms/subgroups/join',
  (roomId: string) => {
    return joinRoomCall(roomId);
  }
);

export const leaveRoom = createAppAsyncThunk(
  'community/rooms/leave',
  (roomId: string) => {
    return leaveRoomCall(roomId);
  }
);

export const moderatorRemoveMemberFromRoom = createAppAsyncThunk(
  'community/rooms/moderator/removeMember',
  (args: { memberId: string; roomId: string }) => {
    return moderatorRemoveMemberFromRoomCall(args.memberId, args.roomId);
  }
);

export const fetchSuggestedRooms = createAppAsyncThunk(
  'identity/getsuggestedrooms',
  (args: { xMsContinuationToken?: string; numrecs?: number }) => {
    return fetchSuggestedRoomsCall(args.xMsContinuationToken, args.numrecs);
  }
);

export const visitRoom = createAppAsyncThunk(
  'identity/room/visit',
  (roomId: string) => {
    return visitRoomCall(roomId);
  }
);

export const fetchPeopleInTheRoom = createAppAsyncThunk(
  'community/rooms/members',
  (arg: {
    roomId: string;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchPeopleInTheRoomCall(
      arg.roomId,
      arg.xMsContinuationToken,
      arg.numrecs
    );
  }
);

export const findLinkedRooms = createAppAsyncThunk(
  'community/rooms/linkedRooms',
  (arg: {
    roomId: string;
    relationshipType: LinkedRoomRelationshipType;
    xMsContinuationToken?: string;
  }) => {
    return findLiknedRoomsCall(
      arg.roomId,
      arg.relationshipType,
      arg.xMsContinuationToken
    );
  }
);

export const fetchPeopleInTheRoomCount = createAppAsyncThunk(
  'community/rooms/membercount',
  (arg: { roomId: string }) => {
    return fetchPeopleInTheRoomCountCall(arg.roomId);
  }
);

export const addMemberToPrivateRoom = createAppAsyncThunk(
  'community/rooms/memberToPrivateRoom',
  (roomCode: string) => {
    return addMemberToPrivateRoomCall(roomCode);
  }
);

export const fetchModeratorsInTheRoom = createAppAsyncThunk(
  'community/rooms/moderatorsInTheRoom',
  (arg: {
    roomId: string;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchModeratorsInTheRoomCall(
      arg.roomId,
      arg.xMsContinuationToken,
      arg.numrecs
    );
  }
);

export const fetchModeratorsInTheRoomCount = createAppAsyncThunk(
  'community/rooms/moderatorsCount',
  (arg: { roomId: string }) => {
    return fetchModeratorsInTheRoomCountCall(arg.roomId);
  }
);

export const fetchContentForCarousel = createAppAsyncThunk(
  'community/rooms/contentCarousel',
  (roomId: string) => {
    return fetchContentForCarouselCall(roomId);
  }
);

export const fetchFeaturedContent = createAppAsyncThunk(
  'community/rooms/featuredContent',
  (roomId: string) => {
    return fetchFeaturedContentCall(roomId);
  }
);

export const fetchFeaturedStory = createAppAsyncThunk(
  'community/rooms/featuredStory',
  (storyId: string) => {
    return fetchFeaturedStoryCall(storyId);
  }
);

export const fetchCurrentMemberInRoom = createAppAsyncThunk(
  'community/rooms/currentMember',
  (roomId: string) => {
    return fetchCurrentMemberInRoomCall(roomId);
  }
);

export const fetchModeratedRooms = createAppAsyncThunk(
  'community/moderator/fetchModeratedRooms',
  () => {
    return fetchModeratedRoomsCall();
  }
);

export const createPrivateRoom = createAppAsyncThunk(
  'community/rooms/createPrivateRoom',
  (createPrivateRoomDto: CreatePrivateRoomDto) => {
    return createPrivateRoomCall(createPrivateRoomDto);
  }
);

export const createSubgroup = createAppAsyncThunk(
  'community/rooms/createSubgroup',
  (createSubgroupDto: CreateSubgroupDto) => {
    return createSubgroupCall(createSubgroupDto);
  }
);

export const createShimMember = createAppAsyncThunk(
  'community/rooms/createShimMember',
  (createShimMemberDto: CreateShimMemberDto) => {
    return createShimMemberCall(createShimMemberDto);
  }
);

export const fetchPrivateRoomCode = createAppAsyncThunk(
  'community/rooms/getPrivateRoomCode',
  (roomId: string) => {
    return fetchPrivateRoomCodeCall(roomId);
  }
);

export const submitConsentToJoinRoom = createAppAsyncThunk(
  'community/rooms/submitConsentToJoinRoom',
  (roomId: string) => {
    return submitConsentToJoinRoomCall(roomId);
  }
);
